import type { IEmployee, IEntraDirectoryEntity, SyncJobStatus, SyncJobSummary } from '@kfolks/types'
import { apiClient } from '@/shared/api/api.client'

// ─── Contractors ────────────────────────────────────────────────────────────

export interface Contractor {
	contractorId: string
	name: string
	code: string
	isActive: boolean
	createdAt: string
	updatedAt: string
}

export interface ContractorFormData {
	name: string
	code: string
	isActive?: boolean
}

// ─── Employees ──────────────────────────────────────────────────────────────

export interface EmployeeFilters {
	displayName?: string
	firstName?: string
	lastName?: string
	contractTypeId?: string
	geoLocationId?: string
	departmentId?: string
	leadershipTypeId?: string
	personTypeId?: string
	contractorId?: string
	isActive?: string
}

export interface EmployeeQueryParams {
	page: number
	limit: number
	sort: string
	order: 'asc' | 'desc'
	filters?: EmployeeFilters
}

export interface ContractorRecord {
	contractorId: string
	name: string
	code: string
	isActive: boolean
}

export interface CreateEmployeeInput {
	displayName: string
	firstName?: string
	lastName?: string
	entraDirectoryId?: string | null
	contractTypeId?: string | null
	geoLocationId?: string | null
	departmentId?: string | null
	leadershipTypeId?: string | null
	personTypeId?: string | null
	contractorId?: string | null
	isActive?: boolean
	comment?: string
}

export interface EntraSearchResult {
	entraDirectoryId: string
	displayName: string
	firstName?: string
	lastName?: string
	email: string | null
	entityType: string
}

// ─── Entra Directory ────────────────────────────────────────────────────────

export interface EntraDirectoryFilters {
	email?: string
	displayName?: string
	entityType?: string
	status?: string
	accountEnabled?: string
	userType?: string
	licenses?: string[]
	hasSignedIn?: string
}

export interface EntraDirectoryQueryParams {
	page: number
	limit: number
	sort: string
	order: 'asc' | 'desc'
	filters?: EntraDirectoryFilters
}

export interface EntraDirectoryListEnvelope {
	data: IEntraDirectoryEntity[]
	total: number
	page: number
	limit: number
	totalPages: number
}

export interface SyncJobResponse {
	jobId: string
	status: SyncJobStatus
	message?: string
}

export interface SyncJobStatusResponse {
	jobId: string
	status: SyncJobStatus
	triggeredBy: string
	summary?: SyncJobSummary
	error?: string
	createdAt?: string
	startedAt?: string
	completedAt?: string
}

// ─── API surface ────────────────────────────────────────────────────────────

function buildEmployeesQuery(params: EmployeeQueryParams): string {
	const qs = new URLSearchParams()
	qs.append('page', params.page.toString())
	qs.append('limit', params.limit.toString())
	qs.append('sort', params.sort)
	qs.append('order', params.order)
	if (params.filters) {
		for (const [key, value] of Object.entries(params.filters)) {
			if (typeof value === 'string' && value.trim() !== '') qs.append(`filters[${key}]`, value)
		}
	}
	return qs.toString()
}

function buildEntraDirectoryQuery(params: EntraDirectoryQueryParams): string {
	const qs = new URLSearchParams()
	qs.append('page', params.page.toString())
	qs.append('limit', params.limit.toString())
	qs.append('sort', params.sort)
	qs.append('order', params.order)
	if (params.filters) {
		for (const [key, value] of Object.entries(params.filters)) {
			if (key === 'licenses') {
				if (Array.isArray(value) && value.length > 0) qs.append('filters[licenseSkuId]', value.join(','))
				continue
			}
			if (typeof value === 'string' && value.trim() !== '') qs.append(`filters[${key}]`, value)
		}
	}
	return qs.toString()
}

export const peopleApi = {
	// Contractors
	createContractor: async (data: ContractorFormData): Promise<Contractor> => {
		const response = await apiClient.post<Contractor>('/contractors', data)
		return response.data
	},
	updateContractor: async ({ id, data }: { id: string; data: Partial<ContractorFormData> }): Promise<Contractor> => {
		const response = await apiClient.put<Contractor>(`/contractors/${id}`, data)
		return response.data
	},
	deleteContractor: async (id: string): Promise<void> => {
		await apiClient.delete(`/contractors/${id}`)
	},

	// Employees
	listEmployees: async (params: EmployeeQueryParams): Promise<IEmployee[]> => {
		const response = await apiClient.get<IEmployee[]>(`/employees?${buildEmployeesQuery(params)}`)
		return response.data || []
	},
	listContractorsRef: async (): Promise<ContractorRecord[]> => {
		const response = await apiClient.get<ContractorRecord[]>('/employees/contractors')
		return response.data || []
	},
	createEmployee: async (data: CreateEmployeeInput): Promise<IEmployee> => {
		const response = await apiClient.post<IEmployee>('/employees', data)
		return response.data
	},
	updateEmployee: async ({
		id,
		data,
	}: {
		id: string
		data: Partial<CreateEmployeeInput & { isActive?: boolean }>
	}): Promise<IEmployee> => {
		const response = await apiClient.put<IEmployee>(`/employees/${id}`, data)
		return response.data
	},
	deleteEmployee: async (id: string): Promise<void> => {
		await apiClient.delete(`/employees/${id}`)
	},
	/**
	 * Get one employee by id. Returns the full record as `Record<string, unknown>`
	 * because the form-autofill consumer (DynamicFormRenderer) projects arbitrary
	 * fields by name; a stricter shape would be cosmetic, not load-bearing.
	 */
	getEmployee: async (id: string): Promise<Record<string, unknown>> => {
		const response = await apiClient.get<Record<string, unknown>>(`/employees/${id}`)
		return response.data
	},
	/**
	 * Display-name search wrapper for the EmployeeAutocomplete picker. Hits
	 * `/employees?filters[displayName]=...&limit=N` — the focused-search URL
	 * the autocomplete uses; distinct from `listEmployees` which requires
	 * full pagination + sort params. Return type widens `IEmployee` with
	 * `email` because the picker endpoint joins it in for display; the field
	 * isn't part of the canonical `IEmployee` shape.
	 */
	searchEmployeesByDisplayName: async (
		query: string,
		limit = 10,
	): Promise<Array<IEmployee & { email?: string | null }>> => {
		const response = await apiClient.get<Array<IEmployee & { email?: string | null }>>(
			`/employees?filters[displayName]=${encodeURIComponent(query)}&limit=${limit}`,
		)
		return response.data || []
	},
	searchEntra: async (query: string): Promise<EntraSearchResult[]> => {
		const response = await apiClient.get<EntraSearchResult[]>(`/entra-directory/search?q=${encodeURIComponent(query)}`)
		return response.data || []
	},

	// Entra Directory
	listEntraDirectory: async (params: EntraDirectoryQueryParams): Promise<EntraDirectoryListEnvelope> => {
		const response = await apiClient.get<EntraDirectoryListEnvelope>(
			`/entra-directory?${buildEntraDirectoryQuery(params)}`,
		)
		if (response.status === 204 || !response.data) {
			return { data: [], total: 0, page: 1, limit: params.limit, totalPages: 0 }
		}
		return response.data
	},
	triggerEntraSync: async (): Promise<SyncJobResponse> => {
		const response = await apiClient.post<SyncJobResponse>('/entra-directory/sync/entra', {})
		return response.data
	},
	getEntraSyncStatus: async (syncJobId: string): Promise<SyncJobStatusResponse> => {
		const response = await apiClient.get<SyncJobStatusResponse>(`/entra-directory/sync/${syncJobId}`)
		return response.data
	},
	listEntraLicenses: async (): Promise<string[]> => {
		const response = await apiClient.get<string[]>('/entra-directory/licenses')
		return response.data || []
	},
}
